import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { RotateCcw, Clock, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export interface RevisionEntry {
  id: string;
  content: string;
  action: string;
  timestamp: Date;
}

interface RevisionHistoryProps {
  revisions: RevisionEntry[];
  onRevert: (content: string) => void;
  onClose: () => void;
}

export const RevisionHistory = ({ revisions, onRevert, onClose }: RevisionHistoryProps) => {
  if (revisions.length === 0) {
    return null;
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'smart_revision': return 'bg-purple-500/10 text-purple-700 dark:text-purple-400';
      case 'expand': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'improve': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'fact_check': return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
      default: return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    }
  };

  const getActionLabel = (action: string) => {
    return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Revision History ({revisions.length})
          </span>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-3">
            {revisions.map((revision, index) => (
              <div
                key={revision.id}
                className="border rounded-lg p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getActionColor(revision.action)} variant="outline">
                        {getActionLabel(revision.action)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(revision.timestamp, { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {revision.content.substring(0, 100)}...
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onRevert(revision.content)}
                    className="flex-shrink-0"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Revert
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
